import './file-upload.scss';

import { Modal } from '@basqet-dashboard/common/components';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
const DROPZONE_ACCEPT = ALLOWED_TYPES.reduce((acc, type) => ({ ...acc, [type]: [] }), {});

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const inferMimeTypeFromName = (name = '') => {
  const extension = name.split('.').pop()?.toLowerCase();
  const map = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    pdf: 'application/pdf'
  };

  return map[extension] || 'application/octet-stream';
};

const getFileType = (fileValue) => {
  if (!fileValue) return '';
  if (typeof fileValue.type === 'string' && fileValue.type.length > 0) return fileValue.type;
  return inferMimeTypeFromName(fileValue.name);
};

const FileUpload = ({
  label,
  description,
  value,
  onChange,
  onDelete,
  onView,
  sampleTitle,
  sampleSubtitle,
  sampleImage,
  maxSizeMB = 5,
  error,
  disabled = false
}) => {
  const [progress, setProgress] = useState(value ? 100 : 0);
  const [uploading, setUploading] = useState(false);
  const [showSample, setShowSample] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewUsesObjectUrl, setPreviewUsesObjectUrl] = useState(false);
  const [typeError, setTypeError] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!value) {
      setProgress(0);
      setUploading(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUsesObjectUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl, previewUsesObjectUrl]);

  const simulateProgress = () => {
    setProgress(0);
    setUploading(true);
    let p = 0;
    intervalRef.current = setInterval(() => {
      p += Math.floor(Math.random() * 20) + 10;
      if (p >= 100) {
        p = 100;
        clearInterval(intervalRef.current);
        setUploading(false);
      }
      setProgress(p);
    }, 100);
  };

  const onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles?.length > 0) {
      const code = rejectedFiles[0].errors?.[0]?.code;
      if (code === 'file-too-large') {
        setTypeError(`File size must not exceed ${maxSizeMB}MB.`);
      } else {
        setTypeError('Only images (JPEG, PNG, GIF, WebP) and PDFs are allowed.');
      }
      return;
    }
    const file = acceptedFiles[0];
    if (!file) return;
    setTypeError(null);
    onChange(file);
    simulateProgress();
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: DROPZONE_ACCEPT,
    maxSize: maxSizeMB * 1024 * 1024,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled
  });

  const handleViewFile = (e) => {
    e.preventDefault();
    if (!value) return;
    onView?.();
    if (previewUrl && previewUsesObjectUrl) URL.revokeObjectURL(previewUrl);

    const remoteUrl = value?.url;
    if (typeof remoteUrl === 'string' && remoteUrl.length > 0) {
      setPreviewUsesObjectUrl(false);
      setPreviewUrl(remoteUrl);
      setShowPreview(true);
      return;
    }

    const localUrl = URL.createObjectURL(value);
    setPreviewUsesObjectUrl(true);
    setPreviewUrl(localUrl);
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    if (previewUrl && previewUsesObjectUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (!previewUsesObjectUrl) {
      setPreviewUrl(null);
    }
    setPreviewUsesObjectUrl(false);
  };

  const fileType = getFileType(value);
  const isImage = !!fileType && fileType.startsWith('image/');
  const isPdf = fileType === 'application/pdf';

  const isEmpty = !value;
  const isUploading = value && uploading;
  const isComplete = value && !uploading && progress === 100;

  return (
    <div className="file-upload">
      {label && <label className="file-upload__label">{label}</label>}
      {description && <p className="file-upload__description">{description}</p>}

      <div
        className={classNames('file-upload__area', {
          'file-upload__area--empty': isEmpty,
          'file-upload__area--uploading': isUploading,
          'file-upload__area--complete': isComplete
        })}>
        {isEmpty && !disabled && (
          <div
            {...getRootProps({
              className: classNames('file-upload__empty', {
                'file-upload__empty--dragging': isDragActive
              }),
              onClick: open,
              role: 'button',
              tabIndex: 0,
              onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') open();
              }
            })}>
            <svg
              className="file-upload__upload-icon"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="file-upload__empty-text">Click to Upload or drag and drop</p>
            <p className="file-upload__size-limit">Max file size: {maxSizeMB}mb</p>
            {sampleTitle && (
              <button
                type="button"
                className="file-upload__sample-link"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSample(true);
                }}>
                Click here to view document sample
              </button>
            )}
          </div>
        )}

        {(isUploading || isComplete) && (
          <div className="file-upload__file-row">
            <svg
              className="file-upload__file-icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <polyline
                points="14 2 14 8 20 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="file-upload__file-info">
              <span className="file-upload__file-name">{value?.name}</span>
              {!value?.isRemote && (
                <span className="file-upload__file-size">{formatFileSize(value?.size || 0)}</span>
              )}
              {isUploading && (
                <div className="file-upload__progress">
                  <div className="file-upload__progress-track">
                    <div className="file-upload__progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="file-upload__progress-text">{progress}%</span>
                </div>
              )}
            </div>
            {isComplete && (
              <div className="file-upload__actions">
                <button type="button" className="file-upload__view-btn" onClick={handleViewFile}>
                  View
                </button>
                {!disabled && (
                  <button
                    type="button"
                    className="file-upload__delete-btn"
                    onClick={onDelete}
                    aria-label="Delete file">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <polyline
                        points="3 6 5 6 21 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <input {...getInputProps()} />

      {(typeError || error) && (
        <span className="file-upload__type-error">{typeError || error}</span>
      )}

      {showSample && sampleTitle && (
        <Modal
          header={{ name: sampleTitle.normal, cursive: sampleTitle.cursive }}
          onModalClose={() => setShowSample(false)}>
          {sampleSubtitle && <p className="file-upload__sample-subtitle">{sampleSubtitle}</p>}
          <div className="file-upload__sample-modal">
            {sampleImage ? (
              <img src={sampleImage} alt="Sample document" className="file-upload__sample-image" />
            ) : (
              <div className="file-upload__sample-placeholder">
                <p>Sample document preview</p>
              </div>
            )}
          </div>
        </Modal>
      )}

      {showPreview && previewUrl && (
        <Modal header={{ name: 'Document', cursive: 'Preview' }} onModalClose={handleClosePreview}>
          <div className="file-upload__preview-modal">
            {isImage && (
              <img src={previewUrl} alt={value?.name} className="file-upload__preview-image" />
            )}
            {isPdf && (
              <iframe src={previewUrl} title={value?.name} className="file-upload__preview-pdf" />
            )}
            {!isImage && !isPdf && (
              <div className="file-upload__preview-fallback">
                <p>{value?.name}</p>
                <a
                  href={previewUrl}
                  download={value?.name}
                  className="file-upload__preview-download">
                  Download file
                </a>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

FileUpload.propTypes = {
  label: PropTypes.node,
  description: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onView: PropTypes.func,
  sampleTitle: PropTypes.shape({
    normal: PropTypes.string,
    cursive: PropTypes.string
  }),
  sampleSubtitle: PropTypes.string,
  sampleImage: PropTypes.string,
  maxSizeMB: PropTypes.number,
  error: PropTypes.string,
  disabled: PropTypes.bool
};

export { FileUpload };
